/**
 * Shared authentication URL building logic
 * This module is environment-agnostic and can be used in both server and client contexts
 */
import { CLIENT_ID } from '@website/constants/auth';

/**
 * Base64url encode a string (works in both Node.js and browser)
 */
function base64UrlEncode(str: string): string {
  // Buffer available in Node.js, btoa in browsers (and Node 16+)
  const base64 = typeof Buffer !== 'undefined' ? Buffer.from(str, 'utf8').toString('base64') : btoa(str);

  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export interface BuildLoginUrlParams {
  /** The full current URL to return to after authentication */
  currentUrl: string;
  /** The origin (protocol + host) for building the callback URL */
  origin: string;
  /** The locale for the login page */
  locale: string;
}

/**
 * Builds the OAuth authorization URL with proper redirect to /auth/callback
 * and state parameter encoding for return URL preservation.
 */
export function buildLoginUrl(params: BuildLoginUrlParams): string {
  const { currentUrl, origin, locale } = params;

  const callbackUrl = `${origin}/auth/callback`;

  const stateObj = { returnTo: currentUrl };
  const state = base64UrlEncode(JSON.stringify(stateObj));

  return `${process.env.NEXT_PUBLIC_AUTH_URL}/authorize?redirect_uri=${encodeURIComponent(
    callbackUrl
  )}&response_type=code&client_id=${CLIENT_ID}&locale=${locale}&state=${encodeURIComponent(state)}`;
}
